import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

// Importar la conexión global de WebSocket
import { globalWebSocket } from './useNotifications';

const useMatchActions = (onNotificationsUpdate) => {
  const [loading, setLoading] = useState(false);
  const { user, checkSession } = useContext(AuthContext);
  const wsRef = useRef(null);
  const [usersMap, setUsersMap] = useState({});
  const navigate = useNavigate();

  // WebSocket setup - usar la conexión global
  useEffect(() => {
    if (globalWebSocket) {
      wsRef.current = globalWebSocket;
    }
  }, []);

  // Fetch users for notifications
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/users');
      const map = {};
      res.data.forEach(u => {
        map[u.id] = u.username;
      });
      setUsersMap(map);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Función para marcar una partida rechazada como procesada
  const closeRejectedMatch = useCallback(async (matchId) => {
    try {
      console.log('🔒 useMatchActions - Marcando partida rechazada como procesada:', matchId);
      
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        throw new Error('No autenticado');
      }
      
      // Marcar en el servidor que la partida está procesada para este usuario
      const response = await api.post(`/api/matches/${matchId}/close-rejected`, {
        status: 'processed' // Usar el estado correcto 'processed'
      });
      console.log('✅ useMatchActions - Partida marcada como procesada con éxito:', response.data);
      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error al marcar partida como procesada:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        matchId
      });
      
      // Si el error es 500, intentamos una segunda vez después de un breve retraso
      if (error.response?.status === 500) {
        console.log('🔄 useMatchActions - Reintentando marcar partida como procesada...');
        try {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar 1 segundo
          const retryResponse = await api.post(`/api/matches/${matchId}/close-rejected`, {
            status: 'processed' // Usar el estado correcto 'processed' en el reintento también
          });
          console.log('✅ useMatchActions - Reintento exitoso:', retryResponse.data);
          return true;
        } catch (retryError) {
          console.error('❌ useMatchActions - Error en el reintento:', {
            error: retryError,
            message: retryError.message,
            response: retryError.response?.data,
            status: retryError.response?.status
          });
        }
      }
      
      return false;
    }
  }, [checkSession]);

  // Approval actions
  const handleApprove = useCallback(async (matchId) => {
    if (loading) return;
    setLoading(true);
    try {
      console.log('⏳ useMatchActions - handleApprove iniciado para partida:', matchId);
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        throw new Error('No autenticado');
      }

      console.log('🔄 useMatchActions - Llamando a API para aprobar partida:', matchId);
      await api.post(`/api/matches/${matchId}/approve`);
      console.log('✅ useMatchActions - Partida aprobada con éxito:', matchId);

      // Forzar actualización de notificaciones
      if (onNotificationsUpdate) {
        console.log('🔄 useMatchActions - Actualizando notificaciones después de aprobar');
        await onNotificationsUpdate();
      }

      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error aprobando partida:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading, checkSession, onNotificationsUpdate]);

  const handleReject = useCallback(async (matchId) => {
    if (loading) return;
    setLoading(true);
    try {
      console.log('⏳ useMatchActions - handleReject iniciado para partida:', matchId);
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        throw new Error('No autenticado');
      }

      // 1. Rechazar la partida
      console.log('🔄 useMatchActions - Llamando a API para rechazar partida:', matchId);
      await api.post(`/api/matches/${matchId}/reject`);
      
      // 2. Obtener datos actualizados
      console.log('🔍 useMatchActions - Obteniendo datos actualizados de la partida:', matchId);
      const matchResponse = await api.get(`/api/matches/${matchId}`);
      const match = matchResponse.data;

      // Verificar que tenemos datos completos de la partida
      if (!match.team1?.players || !match.team2?.players) {
        console.error('❌ useMatchActions - Datos incompletos de la partida rechazada:', match);
        throw new Error('Datos incompletos');
      }

      // 3. Actualizar el estado de aprobación del jugador actual
      const userId = String(user.id);
      const team1Player = match.team1.players.find(p => String(p.user_id) === userId);
      const team2Player = match.team2.players.find(p => String(p.user_id) === userId);
      
      if (team1Player) {
        team1Player.approval_status = 'rejected';
      }
      if (team2Player) {
        team2Player.approval_status = 'rejected';
      }

      // 4. Usar la conexión global de WebSocket
      if (globalWebSocket && globalWebSocket.readyState === WebSocket.OPEN) {
        console.log('📡 useMatchActions - Enviando notificación WebSocket de rechazo');
        const otherTeam = match.team1.players.some(p => p.user_id === user.id) ? match.team2 : match.team1;
        const rejectedByUsername = usersMap[user.id] || 'Usuario desconocido';

        // Get all players that need to be notified (other team + creator)
        const playersToNotify = [...otherTeam.players];
        
        // Always add the creator to the notification list if they are a player
        const creator = match.team1.players.find(p => p.user_id === match.created_by) || 
                       match.team2.players.find(p => p.user_id === match.created_by);
        if (creator && !playersToNotify.some(p => p.user_id === creator.user_id)) {
          playersToNotify.push(creator);
        }

        console.log('👥 useMatchActions - Jugadores a notificar:', playersToNotify);
        console.log('👤 useMatchActions - Usuario que rechaza:', rejectedByUsername, '(ID:', user.id, ')');

        playersToNotify.forEach(player => {
          console.log('📨 useMatchActions - Enviando notificación a jugador:', player.user_id);
          const notification = {
            type: 'match_rejected',
            matchId: match.id,
            rejectedBy: rejectedByUsername,
            userId: player.user_id,
            matchData: {
              id: match.id,
              match_date: match.match_date,
              match_mode: match.match_mode,
              created_by: match.created_by,
              team1: match.team1?.players?.map(p => ({ id: p.user_id, username: p.username })),
              team2: match.team2?.players?.map(p => ({ id: p.user_id, username: p.username }))
            }
          };
          console.log('📤 useMatchActions - Contenido de la notificación:', notification);
          globalWebSocket.send(JSON.stringify(notification));
        });
        console.log('✅ useMatchActions - Notificación enviada correctamente');
      } else {
        console.log('⚠️ useMatchActions - No se pudo enviar notificación WebSocket - Conexión no disponible');
      }

      // 5. Actualizar el estado local
      console.log('✅ useMatchActions - Partida rechazada con éxito:', matchId);

      // Forzar actualización de notificaciones
      if (onNotificationsUpdate) {
        console.log('🔄 useMatchActions - Actualizando notificaciones después de rechazar');
        await onNotificationsUpdate();
      }

      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error rechazando partida:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, loading, usersMap, onNotificationsUpdate]);

  // Rejection handling actions
  const handleRejectionOk = useCallback(async (matchId) => {
    if (loading) return;
    setLoading(true);
    try {
      console.log('⏳ useMatchActions - handleRejectionOk iniciado para partida:', matchId);
      
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        return false;
      }

      console.log('🔄 useMatchActions - Llamando a API para manejar rechazo (OK):', matchId);
      const response = await api.post(`/api/matches/${matchId}/handle-rejection`, { action: 'ok' });
      
      if (response.status === 401) {
        console.log('❌ useMatchActions - Sesión expirada');
        return false;
      }
      
      console.log('✅ useMatchActions - Manejo de rechazo (OK) completado con éxito:', matchId);
      
      // Forzar actualización de notificaciones
      if (onNotificationsUpdate) {
        console.log('🔄 useMatchActions - Actualizando notificaciones después de anular');
        await onNotificationsUpdate();
      }
      
      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error manejando rechazo (OK):', error);
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        console.log('❌ useMatchActions - No autorizado para manejar esta notificación');
        throw new Error('No tienes permiso para aceptar este rechazo');
      } else if (error.response?.status === 401) {
        console.log('❌ useMatchActions - Sesión expirada');
        return false;
      } else {
        console.error('❌ useMatchActions - Error inesperado:', error);
        throw new Error('Ocurrió un error al procesar el rechazo');
      }
    } finally {
      setLoading(false);
    }
  }, [loading, checkSession, onNotificationsUpdate]);

  const handleCancel = useCallback(async (matchId) => {
    if (loading) return;
    setLoading(true);
    try {
      console.log('⏳ useMatchActions - handleCancel iniciado para partida:', matchId);
      
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        throw new Error('No autenticado');
      }

      console.log('🔄 useMatchActions - Llamando a API para cancelar partida:', matchId);
      const response = await api.post(`/api/matches/${matchId}/cancel`);
      
      if (response.status === 401) {
        console.log('❌ useMatchActions - Sesión expirada');
        throw new Error('Sesión expirada');
      }
      
      console.log('✅ useMatchActions - Partida cancelada con éxito:', response.data);
      
      // Forzar actualización de notificaciones
      if (onNotificationsUpdate) {
        console.log('🔄 useMatchActions - Actualizando notificaciones después de cancelar');
        await onNotificationsUpdate();
      }
      
      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error cancelando partida:', error);
      
      if (error.response?.status === 403) {
        throw new Error('No tienes permiso para procesar esta partida');
      } else if (error.response?.status === 401) {
        throw new Error('Sesión expirada - Por favor vuelve a iniciar sesión');
      } else if (error.response?.status === 400) {
        console.log('❌ useMatchActions - Error en la solicitud:', error.response?.data);
        
        // Manejar diferentes tipos de errores 400
        const errorData = error.response?.data;
        if (errorData?.error === 'No se puede cancelar la partida desde el estado actual') {
          throw new Error(`No se puede procesar la partida en su estado actual (${errorData?.details || 'estado inválido'})`);
        } else if (errorData?.error === 'Estado de aprobación inválido') {
          throw new Error('Error interno: Estado de aprobación inválido');
        } else {
          throw new Error(errorData?.error || 'Error al procesar la partida');
        }
      } else {
        console.error('❌ useMatchActions - Error inesperado:', error);
        throw new Error('Ocurrió un error al procesar la partida');
      }
    } finally {
      setLoading(false);
    }
  }, [loading, checkSession, onNotificationsUpdate]);

  const handleCreateNewFromRejected = useCallback(async (match) => {
    if (loading) return;
    setLoading(true);
    try {
      console.log(`🔍 useMatchActions - Verificando datos de partida rechazada:`, match);
      
      const isAuthenticated = await checkSession();
      if (!isAuthenticated) {
        console.log('❌ useMatchActions - Usuario no autenticado');
        throw new Error('No autenticado');
      }

      if (!match.team1?.players || !match.team2?.players) {
        console.error('❌ useMatchActions - Datos de equipos incompletos:', {
          team1: match.team1,
          team2: match.team2
        });
        throw new Error('Datos de equipos incompletos');
      }

      // Primero cancelar la partida original
      try {
        await handleCancel(match.id);
      } catch (error) {
        // Si falla el cancelar, pero es porque ya está cancelada, continuamos
        if (error.response?.status === 400 && error.response?.data?.error?.includes('ya está cancelada')) {
          console.log('⚠️ useMatchActions - Partida ya estaba cancelada, continuando...');
        } else {
          throw error;
        }
      }

      // Preparar los datos para pasar a NewMatchPage
      const matchData = {
        matchMode: match.match_mode,
        tier: match.tier,
        totalMatches: (() => {
          if (match.total_matches) {
            return match.total_matches;
          }
          const team1Score = match.team1?.score || 0;
          const team2Score = match.team2?.score || 0;
          return Math.max(team1Score + team2Score, 1);
        })(),
        team1: {
          players: match.team1.players.map(p => ({
            id: p.user_id,
            username: p.username || usersMap[p.user_id] || 'Usuario',
            selected: true
          })),
          score: match.team1.score || 0,
          decks: match.team1.decks || [],
          victories: match.team1.score || 0
        },
        team2: {
          players: match.team2.players.map(p => ({
            id: p.user_id,
            username: p.username || usersMap[p.user_id] || 'Usuario',
            selected: true
          })),
          score: match.team2.score || 0,
          decks: match.team2.decks || [],
          victories: match.team2.score || 0
        },
        highlights: match.highlights || []
      };
      
      console.log('🚀 useMatchActions - Navegando a NewMatchPage con datos:', matchData);
      
      // Navegar a la página de creación de partida con los datos precargados
      navigate('/new-match', { 
        state: { 
          matchData,
          fromRejected: true,
          originalMatchId: match.id 
        } 
      });

      return true;
    } catch (error) {
      console.error('❌ useMatchActions - Error preparando nueva partida:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loading, checkSession, usersMap, navigate, handleCancel]);

  // Helper functions
  const needsUserApproval = useCallback((match) => {
    if (!user || !match) return false;
    
    try {
      const userId = String(user.id);
      const team1Player = match.team1?.players?.find(p => String(p.user_id) === userId);
      const team2Player = match.team2?.players?.find(p => String(p.user_id) === userId);
      
      return (team1Player?.approval_status === 'pending' || 
              team2Player?.approval_status === 'pending');
    } catch (error) {
      console.error('Error checking approval status:', error);
      return false;
    }
  }, [user]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    loading,
    handleApprove,
    handleReject,
    handleRejectionOk,
    handleCancel,
    handleCreateNewFromRejected,
    closeRejectedMatch,
    needsUserApproval,
    wsRef,
    usersMap
  };
};

export default useMatchActions; 